import { downloadBlob } from '@/utils/download-file';
import { inferMimeTypeFromFileName } from '@/utils/infer-mime-type-from-file-name';
import { computed, ref } from 'vue';

type LoadedPdfSource =
  | Blob
  | string
  | {
      url: string;
      revokeOnClose?: boolean;
    };

type OpenPdfPreviewOptions = {
  title?: string;
  fileName?: string;
  url?: string;
  mimeType?: string;
  revokeOnClose?: boolean;
  load?: () => Promise<LoadedPdfSource>;
  errorMessage?: string;
};

const isOpen = ref(false);
const pdfUrl = ref<string | null>(null);
const title = ref('');
const fileName = ref('document.pdf');
const isLoading = ref(false);
const errorMessage = ref('');
const shouldRevokeOnClose = ref(false);
const mimeType = ref<string>();
const currentRequestId = ref(0);

const previewUrl = computed(() => {
  if (!pdfUrl.value) return '';
  if (mimeType.value === 'application/pdf') {
    return `${pdfUrl.value}#toolbar=0&navpanes=0&view=FitH`;
  }
  return pdfUrl.value;
});

const cleanupPdfUrl = () => {
  if (pdfUrl.value && shouldRevokeOnClose.value) {
    URL.revokeObjectURL(pdfUrl.value);
  }
  pdfUrl.value = null;
  shouldRevokeOnClose.value = false;
  mimeType.value = undefined;
};

const GENERIC_MIME_TYPES = new Set([
  'application/octet-stream',
  'binary/octet-stream',
  'multipart/form-data',
]);

const isGenericMimeType = (mime: string | undefined) =>
  !mime || GENERIC_MIME_TYPES.has(mime.toLowerCase());

const resolveMimeType = (sourceMimeType: string | undefined, fallbackMimeType: string) => {
  if (isGenericMimeType(sourceMimeType)) return fallbackMimeType;
  return sourceMimeType as string;
};

const normalizeLoadedSource = (source: LoadedPdfSource, fallbackMimeType: string) => {
  if (source instanceof Blob) {
    const mimeType = resolveMimeType(source.type, fallbackMimeType);
    // Browsers key off the Blob's type for blob: URLs. Wrong metadata (e.g. multipart/form-data
    // from legacy uploads) makes PDFs open as download instead of in an iframe.
    const needsRetype = !source.type || source.type !== mimeType;
    const blobForObjectUrl = needsRetype ? new Blob([source], { type: mimeType }) : source;
    return {
      url: URL.createObjectURL(blobForObjectUrl),
      revokeOnClose: true,
      mimeType,
    };
  }

  if (typeof source === 'string') {
    return {
      url: source,
      revokeOnClose: source.startsWith('blob:'),
      mimeType: fallbackMimeType,
    };
  }

  return {
    url: source.url,
    revokeOnClose: source.revokeOnClose ?? source.url.startsWith('blob:'),
    mimeType: fallbackMimeType,
  };
};

const setPdfPreviewSource = (source: LoadedPdfSource, fallbackMimeType: string) => {
  cleanupPdfUrl();
  const normalized = normalizeLoadedSource(source, fallbackMimeType);
  pdfUrl.value = normalized.url;
  shouldRevokeOnClose.value = normalized.revokeOnClose;
  mimeType.value = normalized.mimeType;
  isLoading.value = false;
  errorMessage.value = '';
};

const setPdfPreviewError = (message: string) => {
  cleanupPdfUrl();
  isLoading.value = false;
  errorMessage.value = message;
};

const closePdfPreview = () => {
  currentRequestId.value += 1;
  isOpen.value = false;
  isLoading.value = false;
  errorMessage.value = '';
  cleanupPdfUrl();
};

const openPdfPreview = async (options: OpenPdfPreviewOptions) => {
  const requestId = currentRequestId.value + 1;
  currentRequestId.value = requestId;

  cleanupPdfUrl();
  title.value = options.title ?? '';
  fileName.value = options.fileName ?? 'document.pdf';
  // Unknown types must not default to PDF: HEIC/octet-stream blobs then render in an iframe and fail.
  const resolvedFallbackMime =
    (!isGenericMimeType(options.mimeType) ? options.mimeType : undefined) ??
    inferMimeTypeFromFileName(fileName.value) ??
    'application/octet-stream';
  mimeType.value = resolvedFallbackMime;
  errorMessage.value = '';
  isOpen.value = true;

  if (options.url) {
    pdfUrl.value = options.url;
    shouldRevokeOnClose.value = options.revokeOnClose ?? options.url.startsWith('blob:');
    mimeType.value = resolvedFallbackMime;
    isLoading.value = false;
    return;
  }

  if (!options.load) {
    isLoading.value = false;
    errorMessage.value = options.errorMessage ?? '';
    return;
  }

  isLoading.value = true;

  try {
    const loadedSource = await options.load();
    if (currentRequestId.value !== requestId) return;
    setPdfPreviewSource(loadedSource, resolvedFallbackMime);
  } catch {
    if (currentRequestId.value !== requestId) return;
    setPdfPreviewError(options.errorMessage ?? 'The PDF could not be loaded.');
  }
};

const downloadPdf = async () => {
  if (!pdfUrl.value || isLoading.value) return;

  const blob = await fetch(pdfUrl.value).then((response) => response.blob());
  await downloadBlob(blob, fileName.value);
};

export const usePdfPreview = () => {
  return {
    isOpen,
    isLoading,
    errorMessage,
    previewUrl,
    mimeType,
    title,
    openPdfPreview,
    closePdfPreview,
    downloadPdf,
  };
};
